import { useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { authService } from '../services/authService';
import configurationApi from '../services/configurationApi';
import { getMenuItemsForRole } from '../config/menuItems';
import { getHomeRoute } from '../config/roles';
import { BROKER_PORTAL_ROLES, canPerformBrokerMutations, isPendingBrokerCompany } from '../config/brokerAccess';
import NewLoanModal from '../pages/loan/components/NewLoanModal';
import RateLockAdvisorModal from '../pages/loan/components/RateLockAdvisorModal';
import ComplianceFooter from '../components/shared/ComplianceFooter';
import NavRail, { NAV_RAIL_WIDTH } from './piper/NavRail';
import PiperPageHeader, { PAGE_HEADER_SLOT_ID } from './piper/PiperPageHeader';
import PiperRail from './piper/PiperRail';
import { PiperRailProvider } from './piper/PiperRailContext';
import { PiperConsentProvider } from './piper/PiperConsentContext';
import { useAppSelector } from '../store/hooks';
import { roleLabels } from '../config/menuItems';
import usePiperFeatures from '../hooks/usePiperFeatures';

export { PAGE_HEADER_SLOT_ID };

/**
 * AppLayout — Piper three-column shell:
 * 64px dark nav rail · main column on #f5f6f8 with a white page-header bar ·
 * 340px Piper rail (on pages that publish rail content).
 */
const AppLayout = () => {
  const [showNewLoanModal, setShowNewLoanModal] = useState(false);
  const [showRateLockModal, setShowRateLockModal] = useState(false);
  const [exitingImpersonation, setExitingImpersonation] = useState(false);
  const { data: featureFlags = {} } = configurationApi.endpoints.getFeatureFlags.useQuery();
  const piperFeatures = usePiperFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  // Banner must follow the token claim, not the persisted Admin snapshot alone —
  // that snapshot can survive a fresh login and falsely show "Viewing as…".
  const isImpersonating = useAppSelector((state) => !!state.auth.user?.impersonatorSub);
  const impersonatorName = useAppSelector((state) => state.auth.impersonation?.originalUser?.name)
    || currentUser?.impersonatorName;
  const role = currentUser?.role;
  const pendingBroker = isPendingBrokerCompany(currentUser?.brokerCompanyStatus);
  const isBrokerSection = BROKER_PORTAL_ROLES.includes(role);
  const canUseBrokerHeaderActions = canPerformBrokerMutations(role) && !pendingBroker;

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleExitImpersonation = () => {
    setExitingImpersonation(true);
    try {
      const adminUser = authService.exitImpersonation();
      if (!adminUser) {
        // Token says we're impersonating but no Admin snapshot to restore (e.g. cleared
        // by a later login in another tab) — fall back to a full logout.
        authService.logout();
        navigate('/login');
        return;
      }
      navigate(getHomeRoute(adminUser.role));
    } finally {
      setExitingImpersonation(false);
    }
  };

  const menuItems = useMemo(() => {
    return getMenuItemsForRole(role, featureFlags, { pendingBroker });
  }, [featureFlags, pendingBroker, role]);

  const brandLink = getHomeRoute(role);

  // Default page title from the active menu item; loan detail portals its own.
  const pageTitle = useMemo(() => {
    const match = menuItems.find(
      (i) => location.pathname === i.path || location.pathname.startsWith(i.path + '/'),
    );
    return match?.text ?? null;
  }, [menuItems, location.pathname]);

  return (
    <PiperRailProvider>
      <PiperConsentProvider>
        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', backgroundColor: (t) => t.piper?.appBg ?? '#f5f6f8' }}>
          <NavRail
            menuItems={menuItems}
            brandLink={brandLink}
            currentUser={currentUser}
            onLogout={handleLogout}
          />

          {/* Everything right of the nav rail — fixed viewport shell; main column scrolls */}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0, ml: `${NAV_RAIL_WIDTH}px`, overflow: 'hidden' }}>
            <PiperPageHeader
              brandLink={brandLink}
              title={pageTitle}
              showNewLoan={isBrokerSection && canUseBrokerHeaderActions}
              onNewLoan={() => setShowNewLoanModal(true)}
              onOpenAdvisor={isBrokerSection && canUseBrokerHeaderActions ? () => setShowRateLockModal(true) : undefined}
            />

            {isImpersonating && (
              <Alert
                severity="info"
                sx={{ mx: 3, mt: 2, flexShrink: 0, alignItems: 'center' }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    disabled={exitingImpersonation}
                    onClick={handleExitImpersonation}
                  >
                    {exitingImpersonation ? 'Exiting…' : 'Exit impersonation'}
                  </Button>
                }
              >
                Viewing as <strong>{currentUser?.name}</strong>
                {role ? ` (${roleLabels[role] || role})` : ''}
                {impersonatorName ? ` — signed in by ${impersonatorName}` : ''}
              </Alert>
            )}

            {/* Broker pending-approval banner */}
            {isBrokerSection && pendingBroker && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mx: 3, mt: 2, flexShrink: 0 }}>
                Your company is pending approval. Quick Pricer is available now, and full broker features unlock after approval.
              </Alert>
            )}

            {/* Main + Piper rail columns — main is a flex column so pages can own their own scroll (e.g. sticky loan tabs). */}
            <Box sx={{ display: 'flex', flex: 1, alignItems: 'stretch', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
              <Box
                component="main"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  px: 3,
                  py: 2.5,
                }}
              >
                <Box
                  key={location.pathname}
                  className="page-fade-in"
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    // Default page scroll. Loan detail opts out via flex:1 + overflow:hidden and scrolls its tab panel instead.
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Outlet />
                </Box>
              </Box>
              {/* Piper AI master switch — when off, no Piper rail/chat anywhere. */}
              {piperFeatures.enabled && <PiperRail />}
            </Box>

            <Box sx={{ flexShrink: 0 }}>
              <ComplianceFooter />
            </Box>
          </Box>

          {/* Broker-only modals */}
          {isBrokerSection && canUseBrokerHeaderActions && (
            <>
              <RateLockAdvisorModal open={showRateLockModal} onClose={() => setShowRateLockModal(false)} />
              <NewLoanModal
                show={showNewLoanModal}
                onClose={() => setShowNewLoanModal(false)}
                mismoImportAdvisorEnabled={featureFlags.mismoImportAdvisor === true}
                onLoanCreated={(loan) => {
                  setShowNewLoanModal(false);
                  navigate(`/broker/loans/${loan.id}`, {
                    state: {
                      postImportAction: loan.action,
                      pricingFilters: loan.pricingFilters,
                    },
                  });
                }}
              />
            </>
          )}
        </Box>
      </PiperConsentProvider>
    </PiperRailProvider>
  );
};

export default AppLayout;
